import React, {useState, useEffect} from "react";
import {injectIntl} from "react-intl";
import {useSelector} from "react-redux";
import axios from "axios";
import {headerSortingClasses} from "../../../../_metronic/_helpers";
import {Card, CardBody, CardHeader, CardHeaderToolbar} from "../../../../_metronic/_partials/controls";
import {PaginationTable} from "../../../components/PaginationTable/PaginationTable";
import NumberInput from "../../../components/NumberInput";
import {ProductInfoColumn} from "../ColumnsFormatter/ProductInfoColumn";
import {SkuFormatter} from "../ColumnsFormatter/SkuFormatter";
import {fetchAllSuppliers, fetchSuppliers, fetchSupplierClients, updateProductPrices, fetchPriceHistory} from "../AdminPagesCrud";
import {UserActiveColumn} from "../ColumnsFormatter/UserInfoColumn";
import {ProductSupplierColumn} from "../ColumnsFormatter/ProductSupplierFormatter";
import {ProductCategoryColumn} from "../ColumnsFormatter/ProductCategoryFormatter";

// Custom sort caret with text arrows
const customSortCaret = (order, column) => {
    if (!order) return <span style={{ color: '#6c757d', marginLeft: '4px' }}>↕</span>;
    else if (order === "asc") return <span style={{ color: '#28a745', marginLeft: '4px', fontWeight: 'bold' }}>▲</span>;
    else if (order === "desc") return <span style={{ color: '#dc3545', marginLeft: '4px', fontWeight: 'bold' }}>▼</span>;
    return null;
};

// Separate component for price input to ensure proper re-rendering
const PriceInput = React.memo(({ productId, field, originalValue, changedValue, onPriceChange }) => {
    const [localValue, setLocalValue] = React.useState(changedValue !== undefined ? changedValue : originalValue);
    const timeoutRef = React.useRef(null);
    
    // Update local value when original value or changed value changes
    React.useEffect(() => {
        const valueToUse = changedValue !== undefined ? changedValue : originalValue;
        setLocalValue(valueToUse);
    }, [originalValue, changedValue]);
    
    const handleChange = (e) => {
        const newValue = parseFloat(e.target.value) || 0;
        setLocalValue(newValue);
        
        // Debounce the state update to avoid too many re-renders
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            onPriceChange(productId, field, newValue);
        }, 300);
    };
    
    const isInvalid = localValue <= 0 && localValue !== originalValue;
    
    return (
        <NumberInput
            step="0.01"
            min="0.01"
            className="form-control form-control-sm"
            value={localValue}
            onChange={handleChange}
            style={{ 
                width: '80px',
                borderColor: isInvalid ? '#dc3545' : 
                           (changedValue !== undefined || localValue !== originalValue) ? '#28a745' : undefined,
                borderWidth: (changedValue !== undefined || localValue !== originalValue || isInvalid) ? '2px' : undefined,
                backgroundColor: isInvalid ? '#fff5f5' : undefined
            }}
            title={isInvalid ? "Price must be greater than $0" : ""}
        />
    );
});

function ProductPricesAdminPage(props) {
    const { supplierClients } = props

    const initialValues = {
        name: "",
        sku: "",
        description: "",
        storeCode: "",
        supplierId: "",
        categoryId: "",
        unitMeasurement: "CS",
        units: 1,
        unitSize: 1,
        lastPoCost: 0,
        latestLandedCost: 0,
        lowerPriceMargin: 0,
        price: 0,
        discountPrice: 0,
        fob: 0,
        comment: "",
        imagePath: "",
        qtyAvailable: 0,
        qtyOnHand: 0,
        isActive: true,
        defaultUnitOfMeasurement: {},
        unitsOfMeasurement: [],
        exclusiveStoreIds: []
    };

    const {user,accessToken} = useSelector (
        ({auth}) =>({
            user: auth.user != null ? auth.user : null,
            accessToken: auth.authToken,
        })
    )

    const initialData = {
        data: [],
        totalCount: 0,
        pageSize: 10
    }

    const initialHistoryData = {
        data: [],
        totalCount: 0,
        pageSize: 10
    }

    const [data,setData] = useState(initialData)
    const [changedProducts, setChangedProducts] = useState(new Map())
    const [snackbarProductAdmin, setSnackbarProductAdmin] = useState({ show: false, type: 'success', message: '' });
    const [currentFilter, setCurrentFilter] = useState(initialFilter);
    const [priceHistoryData, setPriceHistoryData] = useState(initialHistoryData);
    const [isLoading, setIsLoading] = useState(false);
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);

    useEffect(() => {
        fetchPriceHistoryData({pageNumber: 1, pageSize: 10});
    }, []);

    const fetchProducts = (queryParams) => {
        // Authorization head should be fulfilled in interceptor.
        setIsLoading(true)
        const options = {
            headers: { 'x-auth-token': accessToken},
            validateStatus: status => status < 500,
            params: queryParams
        }
        axios.get(process.env.REACT_APP_API_URL + '/products/tenant', options).then(response =>{
            let temp = response.data;
            
            setData(temp)
            setCurrentFilter(queryParams); // Store current filter
            setIsLoading(false)
        }).catch(error => {
            console.log(error);
            setIsLoading(false)
        });
    }

    const fetchPriceHistoryData = (queryParams) => {
        setIsHistoryLoading(true)
        const offset = (queryParams.pageNumber - 1) * queryParams.pageSize;
        const params = {
            limit: queryParams.pageSize,
            offset: offset
        };

        // Add filter object if search text exists (server-side filtering)
        if (queryParams.filter) {
            params.filter = JSON.stringify(queryParams.filter);
        }

        fetchPriceHistory(accessToken, params).then(response => {
            const historyData = {
                data: response.data.data,
                totalCount: response.data.totalCount,
                pageSize: response.data.data.length
            };
            setPriceHistoryData(historyData);
            setIsHistoryLoading(false)
        }).catch(error => {
            console.error('Error fetching price history (WMS not available):', error);
            setIsHistoryLoading(false)
        });
    }

    // Map MySQL field names to user-friendly display names
    const getFieldDisplayName = (fieldName) => {
        const fieldMap = {
            'sales_price': 'Price',
            'sales_price_esp': 'Discount Price',
            'unit_cost': 'FOB',
            'latest_landed_cost': 'Latest Landed Cost'
        };
        return fieldMap[fieldName] || fieldName;
    };

    // Handle price changes for a specific product
    const handlePriceChange = (productId, field, value) => {
        
        if (!productId) {
            console.error("Product ID is undefined or null");
            return;
        }
        
        const numericValue = parseFloat(value) || 0;
        setChangedProducts(prev => {
            const newMap = new Map(prev);
            if (!newMap.has(productId)) {
                newMap.set(productId, {});
            }
            const productChanges = newMap.get(productId);
            productChanges[field] = numericValue;
            return newMap;
        });
    }

    // Custom formatter for current price display (read-only)
    const CurrentPriceFormatter = (cell, row, rowIndex, formatExtraData) => {
        const field = formatExtraData.field;
        const value = parseFloat(row[field]) || 0;
        return (
            <span style={{ fontSize: '0.9em' }}>
                ${value.toFixed(2)}
            </span>
        );
    };

    // Custom formatter for price input columns
    const PriceInputFormatter = (cell, row, rowIndex, formatExtraData) => {
        const field = formatExtraData.field;
        const productId = row._id;
        const originalValue = parseFloat(row[field]) || 0;
        const changedValue = changedProducts.get(productId)?.[field];
        
        return (
            <PriceInput
                productId={productId}
                field={field}
                originalValue={originalValue}
                changedValue={changedValue}
                onPriceChange={handlePriceChange}
            />
        );
    };

    // Validate that all changed prices are greater than 0
    const validatePrices = () => {
        const invalidPrices = [];
        
        changedProducts.forEach((changes, productId) => {
            Object.entries(changes).forEach(([field, value]) => {
                if (value <= 0) {
                    // Find the product name for better error message
                    const product = data.data.find(p => p._id === productId);
                    const productName = product ? product.name : `Product ${productId}`;
                    invalidPrices.push(`${productName} - ${field}: $${value}`);
                }
            });
        });
        
        return invalidPrices;
    };

    // Save all changed products
    const handleSaveChanges = () => {
        // Validate prices before saving
        const invalidPrices = validatePrices();
        
        if (invalidPrices.length > 0) {
            const errorMessage = `Cannot save changes. The following prices must be greater than $0:\n\n${invalidPrices.join('\n')}`;
            alert(errorMessage);
            return;
        }
        
        const changedProductsArray = Array.from(changedProducts.entries()).map(([productId, changes]) => ({
            productId,
            ...changes
        }));
        
        // Show loading state
        setSnackbarProductAdmin({ show: true, type: 'info', message: 'Saving changes...' });
        
        updateProductPrices(changedProductsArray, accessToken).then((response) => {
            
            // Clear the changed products after successful save
            setChangedProducts(new Map());
            
            // Show success message
            setSnackbarProductAdmin({ show: true, type: 'success', message: 'Product prices saved successfully!' });
            setTimeout(() => setSnackbarProductAdmin({ show: false }), 3000);
            
            // Refresh data after successful save to get updated values
            fetchProducts(currentFilter);

            fetchPriceHistoryData({pageNumber: 1, pageSize: 10});
        }).catch((error) => {

            let errorMessage = 'Failed to save product prices. Please try again.';

            if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
                if (error.response.data.details) {
                    errorMessage += `: ${error.response.data.details}`;
                }
            } else if (error.message) {
                errorMessage = error.message;
            }

            // Show error message
            setSnackbarProductAdmin({ show: true, type: 'error', message: errorMessage });
            setTimeout(() => setSnackbarProductAdmin({ show: false }), 5000);
        });
    }

    const columns = [
        {
            dataField: "sku",
            text: "SKU",
            sort: true,
            sortCaret: customSortCaret,
            headerSortingClasses,
            headerStyle: { 
                color: '#000000 !important',
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #dee2e6',
                fontWeight: '600'
            },
            formatter: SkuFormatter,
        },
        {
            dataField: "name",
            text: "Name",
            sort: true,
            sortCaret: customSortCaret,
            headerSortingClasses,
            headerStyle: { 
                color: '#000000 !important',
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #dee2e6',
                fontWeight: '600'
            },
            formatter: ProductInfoColumn,
        },
        {
            dataField: "supplier",
            text: "Supplier",
            sort: true,
            sortCaret: customSortCaret,
            headerSortingClasses,
            headerStyle: { 
                color: '#000000 !important',
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #dee2e6',
                fontWeight: '600'
            },
            formatter: ProductSupplierColumn
        },
        {
            dataField: "category",
            text: "Category",
            sort: true,
            sortCaret: customSortCaret,
            headerSortingClasses,
            headerStyle: { 
                color: '#000000 !important',
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #dee2e6',
                fontWeight: '600'
            },
            formatter: ProductCategoryColumn
        },
        {
            dataField: "fob",
            text: "FOB",
            headerSortingClasses,
            headerStyle: { 
                color: '#000000 !important',
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #dee2e6',
                fontWeight: '600'
            },
            formatter: CurrentPriceFormatter,
            formatExtraData: { field: 'fob' }
        },
        {
            dataField: "fob",
            text: "New FOB",
            headerSortingClasses,
            headerStyle: { 
                color: '#000000 !important',
                backgroundColor: '#e8f5e8',
                borderBottom: '1px solid #dee2e6',
                fontWeight: '600'
            },
            formatter: PriceInputFormatter,
            formatExtraData: { field: 'fob' }
        },
        {
            dataField: "latestLandedCost",
            text: "Latest Landed Cost",
            headerSortingClasses,
            headerStyle: { 
                color: '#000000 !important',
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #dee2e6',
                fontWeight: '600'
            },
            formatter: CurrentPriceFormatter,
            formatExtraData: { field: 'latestLandedCost' }
        },
        {
            dataField: "latestLandedCost",
            text: "New Latest Landed Cost",
            headerSortingClasses,
            headerStyle: { 
                color: '#000000 !important',
                backgroundColor: '#e8f5e8',
                borderBottom: '1px solid #dee2e6',
                fontWeight: '600'
            },
            formatter: PriceInputFormatter,
            formatExtraData: { field: 'latestLandedCost' }
        },
        {
            dataField: "price",
            text: "Price",
            headerSortingClasses,
            headerStyle: { 
                color: '#000000 !important',
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #dee2e6',
                fontWeight: '600'
            },
            formatter: CurrentPriceFormatter,
            formatExtraData: { field: 'price' }
        },
        {
            dataField: "price",
            text: "New Price",
            headerSortingClasses,
            headerStyle: { 
                color: '#000000 !important',
                backgroundColor: '#e8f5e8',
                borderBottom: '1px solid #dee2e6',
                fontWeight: '600'
            },
            formatter: PriceInputFormatter,
            formatExtraData: { field: 'price' }
        },
        {
            dataField: "discountPrice",
            text: "Discount Price",
            headerSortingClasses,
            headerStyle: { 
                color: '#000000 !important',
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #dee2e6',
                fontWeight: '600'
            },
            formatter: CurrentPriceFormatter,
            formatExtraData: { field: 'discountPrice' }
        },
        {
            dataField: "discountPrice",
            text: "New Discount Price",
            headerSortingClasses,
            headerStyle: { 
                color: '#000000 !important',
                backgroundColor: '#e8f5e8',
                borderBottom: '1px solid #dee2e6',
                fontWeight: '600'
            },
            formatter: PriceInputFormatter,
            formatExtraData: { field: 'discountPrice' }
        },
    ];

    // Force columns to re-render when changedProducts changes
    const columnsWithKey = React.useMemo(() => columns, [changedProducts]);

    const sizePerPageList = [
        { text: "5", value: 5 },
        { text: "10", value: 10 },
        { text: "15", value: 15 }
    ]

    const initialFilter = {
        filter: {
            name:"",
            category:"",
            sku: "",
            supplier: ""
        },
        sortOrder: "asc",
        sortField: "name",
        pageNumber: 1,
        pageSize: 10
    }

    const sorting = [{dataField: "name",order:"asc"}]
    const pagination = {custom: true, sizePerPageList: sizePerPageList}
    const tableClasses = "table table-head-custom table-head-bg table-borderless table-vertical-center"

    const historyColumns = [
        {
            dataField: "product_name",
            text: "Product Name",
            headerStyle: {
                color: '#000000 !important',
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #dee2e6',
                fontWeight: '600'
            }
        },
        {
            dataField: "field_changed",
            text: "Field Changed",
            headerStyle: {
                color: '#000000 !important',
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #dee2e6',
                fontWeight: '600'
            },
            formatter: (cell) => (
                <span className="badge badge-secondary">
                    {getFieldDisplayName(cell)}
                </span>
            )
        },
        {
            dataField: "old_value",
            text: "Old Value",
            headerStyle: {
                color: '#000000 !important',
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #dee2e6',
                fontWeight: '600',
                textAlign: 'right'
            },
            formatter: (cell) => (
                <div className="text-right">${parseFloat(cell).toFixed(2)}</div>
            )
        },
        {
            dataField: "new_value",
            text: "New Value",
            headerStyle: {
                color: '#000000 !important',
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #dee2e6',
                fontWeight: '600',
                textAlign: 'right'
            },
            formatter: (cell) => (
                <div className="text-right">${parseFloat(cell).toFixed(2)}</div>
            )
        },
        {
            dataField: "changed_by",
            text: "Changed By",
            headerStyle: {
                color: '#000000 !important',
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #dee2e6',
                fontWeight: '600'
            }
        },
        {
            dataField: "created_at",
            text: "Date",
            headerStyle: {
                color: '#000000 !important',
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #dee2e6',
                fontWeight: '600'
            },
            formatter: (cell) => new Date(cell).toLocaleString()
        }
    ];

    const historyPagination = {custom: true, sizePerPageList: sizePerPageList}
    const historyInitialFilter = {
        filter: {
            searchText: ""
        },
        sortOrder: "desc",
        sortField: "created_at",
        pageNumber: 1,
        pageSize: 10
    };

    const prepareHistoryFilter = (queryParams, values) => {
        const {searchText} = values;
        const newQueryParams = {...queryParams};
        const filter = {};

        filter.searchText = searchText ?? "";

        newQueryParams.filter = filter;
        return newQueryParams;
    };

    const prepareFilter = (queryParams,values) => {
        const {searchText} = values;
        const newQueryParams = {...queryParams};
        const filter = {};

        filter.name = searchText ?? "";
        filter.category = searchText ?? "";
        filter.sku = searchText ?? "";
        filter.supplier = searchText ?? "";

        newQueryParams.filter = filter;
        return newQueryParams;
    }

    return (
        <>
            <style>
                {`
                    .table.table-head-custom thead tr th {
                        color: #000000 !important;
                    }
                    
                    /* Subtle styling for sorted columns */
                    .table.table-head-custom thead tr th.sortable-active {
                        background-color: #f8f9fa !important;
                        border-bottom: 1px solid #dee2e6 !important;
                        font-weight: 600 !important;
                    }
                `}
            </style>
            <Card>
                <CardHeader title="Product Prices Admin">
                    <CardHeaderToolbar>
                        <button 
                            type="button" 
                            className="btn btn-success" 
                            onClick={handleSaveChanges}
                            disabled={changedProducts.size === 0}
                            title={changedProducts.size === 0 ? 'No changes to save' : 'Save all changes'}
                        >
                            Save Changes ({changedProducts.size})
                        </button>
                        {validatePrices().length > 0 && (
                            <i 
                                className="fas fa-exclamation-triangle ml-2" 
                                style={{ 
                                    color: '#ffc107', 
                                    fontSize: '16px',
                                    cursor: 'pointer'
                                }}
                                title={`Cannot save: ${validatePrices().join(', ')}`}
                            />
                        )}
                    </CardHeaderToolbar>
                </CardHeader>
                <CardBody>
                    <PaginationTable
                        data={data}
                        columns={columnsWithKey}
                        pagination={pagination}
                        isLoading={isLoading}
                        sorting={sorting}
                        tableClasses={tableClasses}
                        fetcher={fetchProducts}
                        filterFunction={prepareFilter}
                        initialFilter={initialFilter}/>
                </CardBody>
            </Card>

            {/* Price History Card - Only show if there's history data */}
            {priceHistoryData.totalCount > 0 && (
                <Card className="mt-5">
                    <CardHeader title="Price Change History"></CardHeader>
                    <CardBody>
                        <PaginationTable
                            data={priceHistoryData}
                            columns={historyColumns}
                            pagination={historyPagination}
                            isLoading={isHistoryLoading}
                            sorting={[]}
                            tableClasses={tableClasses}
                            fetcher={fetchPriceHistoryData}
                            filterFunction={prepareHistoryFilter}
                            initialFilter={historyInitialFilter}/>
                    </CardBody>
                </Card>
            )}

            {/* Snackbar for success message */}
            {snackbarProductAdmin.show && (
                <div className="position-fixed" style={{ top: '20px', right: '20px', zIndex: 9999 }}>
                    <div className={`alert alert-${snackbarProductAdmin.type === 'error' ? 'danger' : snackbarProductAdmin.type} alert-dismissible fade show`} role="alert">
                        {snackbarProductAdmin.message}
                        <button type="button" className="close" onClick={() => setSnackbarProductAdmin({ show: false })}>
                            <span>&times;</span>
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}

export default injectIntl(ProductPricesAdminPage); 